import React, { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, ModalBody, ModalFooter, ModalHeader, Button, SpinningButton } from "Components/ReactstrapSlim";
import { InputFL, PasswordInputFL, ValidationSummary } from "Components/Inputs";
import { Module, ModuleCaption } from "Components/Module";
import { AccountClient, ChangeEmailModel, ChangePasswordModel, IChangeEmailModel, IChangePasswordModel } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import AccountContext from "../../AccountContext";
import nameof from "Utils/nameof";
import UserContext from "features/EnterprisePortal/UserContext";

interface AccountInformationModel {
   email: string;
   password: string;
}

export interface AccountInformationProps {  
}

const ChangeEmailSchema: yup.SchemaOf<IChangeEmailModel> = yup.object({
   skipPasswordValidation: yup.boolean().defined(),
   email: yup.string().required("Email is required").email("Email is not valid").defined(),
   confirmationPassword: yup.string().when(nameof<IChangeEmailModel>("skipPasswordValidation"),
      { is: false, then: x => x.required("Confirmation password is required").defined() })
});

const ChangePasswordFormSchema: yup.SchemaOf<IChangePasswordModel> = yup.object({
   skipPasswordValidation: yup.boolean().defined(),
   currentPassword: yup.string()
      .when(nameof<IChangePasswordModel>("skipPasswordValidation"),
      { is: false, then: x => x.required("Current password is required").defined() }),
   newPassword: yup.string()
      .required("New password is required")
      .min(LiquidSpaceConfig.NewMinPasswordLength, "Password must be at least ${min} characters long")
      .max(LiquidSpaceConfig.MaxPasswordLength, "Enter no more than ${max} characters")
      .passwordStrength()
      .test("no-spaces", "Password shouldn't contain spaces", (value) =>
         !value || !value.includes(" "))
      .notOneOf([yup.ref("oldPassword")], "Password must be different from the current")
      .defined(),
   confirmPassword: yup.string()
      .required("Confirmation password is required")
      .oneOf([yup.ref("newPassword"), null], "Passwords must match")
      .defined(),
});


export const AccountInformation: React.FC<AccountInformationProps> = () => {
   const { user } = useContext(UserContext);    
   const accountContext = useContext(AccountContext);
   const accountClient = useApiClient(AccountClient);
   const [isSaving, setIsSaving] = useState<boolean>(false);
   const [serverErrors, setServerErrors] = useState<Array<[string, string]>>([]);
   const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
   const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
   const [isEmailChangeRequestModalOpen, setIsEmailChangeRequestModalOpen] = useState(false);
   const [isEmailChangeRequestSubmitted, setIsEmailChangeRequestSubmitted] = useState(false);
   const emailChangeConfirmBtnRef = useRef<HTMLButtonElement>(null);

   const togglePasswordModal = () => setIsPasswordModalOpen(!isPasswordModalOpen);
   const toggleEmailModal = () => setIsEmailModalOpen(!isEmailModalOpen);
   const toggleEmailChangeRequestModal = () => {
      setIsEmailChangeRequestModalOpen(false);
      setIsEmailChangeRequestSubmitted(false);
   }

   const oldPassword = "********";
   const skipPasswordValidation = user.ssoLoginType !== null && user.ssoLoginType !== undefined;

   const formMethodsAccountInfo = useForm<AccountInformationModel>({
      defaultValues: {
         email: user.email || undefined,
         password: oldPassword
      },
      mode: "onTouched",
   })

   const formMethodsChangeEmail = useForm<IChangeEmailModel>({
      defaultValues: {
         skipPasswordValidation: skipPasswordValidation,
         email: user.email || undefined,
      },
      mode: "onTouched",
      resolver: yupResolver(ChangeEmailSchema)
   })

   const formMethodsChangePassword = useForm<IChangePasswordModel>({
      defaultValues: {
         skipPasswordValidation: skipPasswordValidation
      },
      mode: "onTouched",
      resolver: yupResolver(ChangePasswordFormSchema)
   })

   const changePassword = async () => {
      if (isSaving)
         return;
      setIsSaving(true);
      setServerErrors([]);
      try {
         const isValid = await formMethodsChangePassword.trigger();
         if (isValid) {
            const formValues = formMethodsChangePassword.getValues();

            const changePasswordModel: ChangePasswordModel = ChangePasswordModel.fromJS(formValues)

            const changeResult = await accountClient.changePassword(changePasswordModel);

            if (changeResult.success) {               
               setIsPasswordModalOpen(false);
            }
         }
      } catch (err: any) {
         if (err.errorMessage) {
            setServerErrors([["newPassword", err.errorMessage]]);
         }
         if (err.validationErrors  && err.validationErrors.length) {            
            setServerErrors(err.validationErrors.map(e => ["newPassword", e]));
         }         
      } finally {
         setIsSaving(false);
      }
   }

   const onChangeEmail = () => {
      if(user.isSuperUser)
         setIsEmailModalOpen(true);
      else
         setIsEmailChangeRequestModalOpen(true);
   }

   const changeEmail = async () => {
      if (isSaving)
         return;
      setIsSaving(true);
      setServerErrors([]);
      try {
         const isValid = await formMethodsChangeEmail.trigger();
         if (isValid) {
            const formValues = formMethodsChangeEmail.getValues();

            const changeEmailModel: ChangeEmailModel = ChangeEmailModel.fromJS(formValues);

            const abortController = new AbortController();

            const changeResult = await accountClient.changeEmail(changeEmailModel, abortController.signal);
            if (changeResult.success) {                
               if(accountContext.portalUrl)
                  window.location.href = accountContext.portalUrl + "/settings";
               else
                  window.location.href = "/";
            }
         } else {
            console.log(formMethodsChangeEmail.formState.errors);
         }
      } catch (err: any) {
         if (err.errorMessage) {
            setServerErrors([["email", err.errorMessage]]);
         }
         if (err.validationErrors  && err.validationErrors.length) {            
            setServerErrors(err.validationErrors.map(e => ["email", e]));
         }         
      } finally {
         setIsSaving(false);
      }
   }

   const changeEmailRequest = async () => {
      setIsSaving(true);
      try {
         const response = await accountClient.changeEmailRequest();
         if (response.success) {
            setIsEmailChangeRequestSubmitted(true);
            if (emailChangeConfirmBtnRef.current) {
               emailChangeConfirmBtnRef.current.focus();
            }
         }
      } catch (err: any) {
         setServerErrors([["email", err.errorMessage ?? "Unexpected server error"]]);
      } finally {
         setIsSaving(false);
      }
   }

   useEffect(() => {
      if(isEmailModalOpen) {
         formMethodsChangeEmail.reset();
      }
   }, [isEmailModalOpen]);

   useEffect(() => {
      if(isPasswordModalOpen) {
         formMethodsChangePassword.reset();
      }
   }, [isPasswordModalOpen])

   return (
      <Module loading={false} fluid className={classNames("module-account-information")}>
         <ModuleCaption className="mb-px8">
            Account Information
         </ModuleCaption>
         <div className="account-information-wrapper">
            <div className="account-row">
               <InputFL disabled control={formMethodsAccountInfo.control} name="email" placeholder="Email" bsSize={"lg"} className="mt-px16" autoComplete="off"  />
               <a href="#" onClick={(e) => {e.preventDefault(); onChangeEmail();}}>Edit</a>
            </div>
            <div className="account-row">
               <PasswordInputFL disabled control={formMethodsAccountInfo.control} name="password" placeholder="Password" className="mt-px16" bsSize={"lg"} autoComplete="off"  />
               <a href="#" onClick={(e) => {e.preventDefault(); setIsPasswordModalOpen(true)}}>Edit</a>               
            </div>            
         </div>
         <Modal isOpen={isEmailModalOpen} toggle={toggleEmailModal}>
            <ModalHeader className="h3" toggle={toggleEmailModal}>Change Email</ModalHeader>
            <ModalBody className="ls-modal-body">            
               <ValidationSummary errors={formMethodsChangeEmail.formState.errors} serverErrors={serverErrors} />
               <input type="hidden" {...formMethodsChangeEmail.register("skipPasswordValidation")} />
               <InputFL control={formMethodsChangeEmail.control} name="email" className="scpf-input" placeholder="Email" />
               {!skipPasswordValidation && <PasswordInputFL control={formMethodsChangeEmail.control} name="confirmationPassword" placeholder="Password Confirmation" className="scpf-input" autoComplete="off" />}
            </ModalBody>
            <ModalFooter>
               <Button color="secondary" onClick={() => setIsEmailModalOpen(false)} className="scpf-cta">Cancel</Button>
               <SpinningButton isLoading={isSaving} onClick={() => changeEmail()} color="primary" size="md" className="scpf-cta">Save</SpinningButton>
            </ModalFooter>
         </Modal>
         <Modal centered isOpen={isEmailChangeRequestModalOpen} toggle={toggleEmailChangeRequestModal}>
            <ModalHeader toggle={toggleEmailChangeRequestModal}>Change Email</ModalHeader>
            <ModalBody className="ls-modal-body">
            {
               isEmailChangeRequestSubmitted ? <>
                  <div>Email change instructions were sent to your existing email address.</div>
               </> : <>
                  <ValidationSummary serverErrors={serverErrors} />
                  <div className="pb-px8">This operation will send an email to confirm the existing address. Follow the instructions in that email to complete this process.</div>
                  <div>Please contact <a href="mailto:support@liquidspace.com">support@liquidspace.com</a> for assistance changing your email address.</div>
               </>
            }
            </ModalBody>
            <ModalFooter>
            {
               isEmailChangeRequestSubmitted ? <>
                  <Button ref={emailChangeConfirmBtnRef} type="button" color="primary" onClick={() => toggleEmailChangeRequestModal()}>Ok</Button>
               </> : <>
                  <Button type="button" color="secondary" onClick={() => toggleEmailChangeRequestModal()}>Cancel</Button>
                  <SpinningButton isLoading={isSaving} onClick={() => changeEmailRequest()} color="primary" size="md" className="scef-cta">Continue</SpinningButton>
               </>
            }
            </ModalFooter>
         </Modal>
         <Modal isOpen={isPasswordModalOpen} toggle={togglePasswordModal}>
            <ModalHeader className="h3" toggle={togglePasswordModal}>Change Password</ModalHeader>
            <ModalBody className="ls-modal-body">
               <ValidationSummary errors={formMethodsChangePassword.formState.errors} serverErrors={serverErrors} />
               <input type="hidden" {...formMethodsChangePassword.register("skipPasswordValidation")} />
               {!skipPasswordValidation && <PasswordInputFL control={formMethodsChangePassword.control} name="currentPassword" placeholder="Current password" className="scpf-input" autoComplete="off" />}
               <PasswordInputFL control={formMethodsChangePassword.control} name="newPassword" placeholder="New password" className="scpf-input" autoComplete="off" />
               <PasswordInputFL control={formMethodsChangePassword.control} name="confirmPassword" placeholder="Confirm new password" className="scpf-input" autoComplete="off" />
            </ModalBody>
            <ModalFooter>
               <Button color="secondary" onClick={() => setIsPasswordModalOpen(false)} className="scpf-cta">Cancel</Button>
               <SpinningButton isLoading={isSaving} onClick={() => changePassword()} color="primary" size="md" className="scpf-cta">Save</SpinningButton>
            </ModalFooter>
         </Modal>
      </Module>
   );
}